import {
  Box,
  Button,
  Flex,
  Paper,
  Text,
  Title,
  Input,
  Select,
  Grid,
  ActionIcon,
  Center,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import useProfileStore from "../../store/useProfileStore";
import { IconPlus } from "../../shared/svg/AddIcon";

import {
  addEducation,
  deleteEducation,
  updateEducation,
} from "../../api/profileEditingApi";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { EducationEdit } from "../../types/profile-editing";
import {
  compareDates,
  getDaysInMonth,
  getMonths,
  getYears,
  isDateValid,
} from "./editUtils";
import { notifications } from "@mantine/notifications";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import { useMediaQuery } from "@mantine/hooks";
import ConfirmDelete from "../../utils/components/ConfirmDelete";

interface Props {
  setChanges: (t: boolean) => void;
}

const EducationEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const [educations, setEducations] = useState<
    (EducationEdit & { isNew?: boolean })[]
  >([]);
  const [originalEducations, setOriginalEducations] = useState<EducationEdit[]>(
    []
  );
  const { profile, fetchProfile, setUserMarketChanges } = useProfileStore();
  const matches = useMediaQuery("(max-width: 650px)");

  const [formErrors, setFormErrors] = useState<
    Record<number, Record<string, string>>
  >({});

  const years = getYears();
  const months = getMonths();

  useEffect(() => {
    if (profile?.educations?.length) {
      setEducations(profile.educations);
      setOriginalEducations(profile.educations);
    }
  }, [profile]);

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleChange = (
    index: number,
    field: keyof EducationEdit | "month" | "year" | "day",
    value: string,
    dateType?: "start_date" | "end_date"
  ) => {
    setEducations((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        if (field === "month" || field === "year" || field === "day") {
          const dateField =
            dateType === "start_date" ? item.start_date : item.end_date;
          return {
            ...item,
            [dateType!]: {
              ...dateField,
              [field]: Number(value),
            },
          };
        }

        return {
          ...item,
          [field]: value,
        };
      })
    );

    setFormErrors((prev) => {
      const updated = { ...prev };
      if (updated[index]?.[field]) {
        delete updated[index][field];
      }
      return updated;
    });
  };

  const handleAddEducation = () => {
    const hasUnsaved = educations.some((e) => e.isNew);

    if (hasUnsaved) return;

    const newEducation: EducationEdit & { isNew?: boolean } = {
      // id: 0,
      institution: "",
      location: "",
      degree: "Other",
      field_of_study: "",
      start_date: { day: null, month: null, year: null },
      end_date: { day: null, month: null, year: null },
      is_current: false,
      isNew: true,
    };

    setEducations((prev) => [...prev, newEducation]);
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSave = async (
    index: number,
    edu: EducationEdit & { isNew?: boolean }
  ) => {
    const errors: Record<string, string> = {};

    if (edu.start_date?.day && !isDateValid(edu.start_date)) {
      errors.start_date = t("invalid_date");
    }
    if (!edu.is_current && edu.end_date?.day && !isDateValid(edu.end_date)) {
      errors.end_date = t("invalid_date");
    }

    if (!edu.is_current && !compareDates(edu.start_date, edu.end_date)) {
      errors.date_range = t("start_date_after_end");
    }

    if (!edu.institution?.trim()) {
      errors.institution = t("field_required");
    }
    if (!edu.field_of_study?.trim()) {
      errors.field_of_study = t("field_required");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors((prev) => ({ ...prev, [index]: errors }));
      return;
    }

    try {
      let savedEdu;

      const payload = {
        ...edu,
        start_date:
          edu.start_date === null
            ? { day: null, month: null, year: null }
            : {
                day:
                  edu.start_date.day?.toString() === "0"
                    ? null
                    : edu.start_date.day,
                month:
                  edu.start_date.month?.toString() === "0"
                    ? null
                    : edu.start_date.month,
                year:
                  edu.start_date.year?.toString() === "0"
                    ? null
                    : edu.start_date.year,
              },

        end_date:
          edu.end_date === null
            ? { day: null, month: null, year: null }
            : {
                day:
                  edu.end_date.day?.toString() === "0"
                    ? null
                    : edu.end_date.day,
                month:
                  edu.end_date.month?.toString() === "0"
                    ? null
                    : edu.end_date.month,
                year:
                  edu.end_date.year?.toString() === "0"
                    ? null
                    : edu.end_date.year,
              },
      };

      delete payload.isNew;

      if (!edu.isNew) {
        savedEdu = await updateEducation(payload);
        setChanges(true);
        setUserMarketChanges(true);
        notifications.show({
          title: t("success"),
          message: t("changed_successfully"),
          icon: <EditIconSmall />,
          color: "#CEB7FF",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      } else {
        const { id, isNew, ...rest } = payload;
        savedEdu = await addEducation(rest);
        setChanges(true);
        setUserMarketChanges(true)
        notifications.show({
          title: t("success"),
          message: t("add_successfully"),
          icon: <CheckIcon />,
          color: "green",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      }

      if (!savedEdu.id) {
        throw new Error("Saved education does not have an id");
      }

      setEducations((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...savedEdu,
          isNew: false,
        } as EducationEdit & { isNew?: boolean };
        return updated;
      });
      setOriginalEducations((prev) => {
        const updated = [...prev];
        updated[index] = savedEdu;
        return updated;
      });

      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      // await fetchProfile();
    } catch (error: any) {
      if (error.errors) {
        setFormErrors((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            ...error.errors,
          },
        }));
      }
    }
  };

  const handleDelete = async (index: number, id?: number) => {
    if (id) {
      try {
        await deleteEducation(id);
        // await fetchProfile();
        setChanges(true);
        setUserMarketChanges(true)
      } catch (err) {
        console.error("Error deleting education:", err);
      }
    }

    setEducations((prev) => prev.filter((_, i) => i !== index));
    setOriginalEducations((prev) => prev.filter((_, i) => i !== index));
    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });
    setFormErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const isEducationChanged = (
    current: EducationEdit & { isNew?: boolean },
    original?: EducationEdit
  ) => {
    if (!original) return true; // Якщо оригінальний об'єкт відсутній, вважаємо, що зміни є

    const keys: (keyof EducationEdit)[] = [
      "institution",
      "location",
      "degree",
      "field_of_study",
      "start_date",
      "end_date",
      "is_current",
    ];

    return keys.some((key) => {
      if (key === "start_date" || key === "end_date") {
        return (
          current[key]?.month !== original[key]?.month ||
          current[key]?.year !== original[key]?.year ||
          current[key]?.day !== original[key]?.day
        );
      }
      return current[key] !== original[key];
    });
  };

  const handleIsCurrentChange = (index: number, checked: boolean) => {
    setEducations((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        return {
          ...item,
          is_current: checked,
          end_date: checked
            ? { day: null, month: null, year: null }
            : { ...item.end_date },
        };
      })
    );

    // Очистимо помилки по end_date якщо є
    setFormErrors((prev) => {
      const updated = { ...prev };
      if (updated[index]?.end_date) {
        delete updated[index].end_date;
      }
      return updated;
    });
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="lg"
        gap={matches ? 20 : 10}
        direction={matches ? "column" : "row"}
      >
        <Title size={32}>{t("education")}</Title>

        <Button
          leftSection={<IconPlus />}
          onClick={handleAddEducation}
          variant="light"
        >
          {t("add_education")}
        </Button>
      </Flex>

      {educations.length === 0 ? (
        <Center py="xl">
          <Paper
            radius="md"
            p="xl"
            withBorder
            shadow="sm"
            style={{ maxWidth: 1000, width: "100%" }}
          >
            <Flex
              align="center"
              w="100%"
              px={20}
              gap={matches ? 20 : 40}
              direction={matches ? "column" : "row"}
            >
              <Title
                order={2}
                fw={700}
                w={matches ? "100%" : "33%"}
                ta="left"
                style={{ fontSize: "1.5rem" }}
              >
                {t("nothing_here")}
              </Title>
              <Text
                size="xl"
                c="dimmed"
                w={matches ? "100%" : "66%"}
                style={{ fontSize: "1.125rem" }}
              >
                {t("no_education")}
              </Text>
            </Flex>
          </Paper>
        </Center>
      ) : (
        <Flex direction="column" gap={30}>
          {educations.map((edu, index) => {
            const originalEducation = originalEducations[index];

            const isChanged = isEducationChanged(edu, originalEducation);
            const handleReset = (index: number) => {
              setFormErrors((prev) => {
                const updated = { ...prev };

                delete updated[index];

                return updated;
              });

              if (!originalEducation) return;

              setEducations((prev) =>
                prev.map((item, idx) =>
                  idx === index ? { ...originalEducation, isNew: false } : item
                )
              );
            };

            return (
              <Paper
                withBorder
                p={24}
                radius="md"
                key={edu.id}
                style={{ backgroundColor: "#f5f3fa", position: "relative" }}
              >
                <Grid gutter={32}>
                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Input.Wrapper
                      label={t("institution")}
                      withAsterisk
                      error={formErrors[index]?.institution}
                    >
                      <Input
                        value={edu.institution || ""}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "institution",
                            e.currentTarget.value
                          )
                        }
                        required
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Input.Wrapper
                      withAsterisk
                      label={t("field_of_study")}
                      error={formErrors[index]?.field_of_study}
                    >
                      <Input
                        value={edu.field_of_study || ""}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "field_of_study",
                            e.currentTarget.value
                          )
                        }
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Input.Wrapper label={t("degree")}>
                      <Select
                        value={edu.degree || "Other"}
                        onChange={(value) =>
                          handleChange(index, "degree", value!)
                        }
                        data={[
                          {
                            value: "HighSchool",
                            label: t("education-edit.highSchool"),
                          },
                          {
                            value: "Associate",
                            label: t("education-edit.associate"),
                          },
                          {
                            value: "Bachelor",
                            label: t("education-edit.bachelor"),
                          },
                          {
                            value: "Master",
                            label: t("education-edit.master"),
                          },
                          { value: "PhD", label: t("education-edit.phd") },
                          {
                            value: "Diploma",
                            label: t("education-edit.diploma"),
                          },
                          {
                            value: "Certificate",
                            label: t("education-edit.certificate"),
                          },
                          { value: "Other", label: t("education-edit.other") },
                        ]}
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Input.Wrapper
                      label={t("location")}
                      error={formErrors[index]?.location}
                    >
                      <Input
                        value={edu.location || ""}
                        onChange={(e) =>
                          handleChange(index, "location", e.currentTarget.value)
                        }
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  {/* Start Date */}
                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Text fw={500} mb={4}>
                      {t("start_date")}
                    </Text>
                    <Flex gap="sm">
                      <Select
                        placeholder={t("year")}
                        data={years}
                        value={
                          edu.start_date?.year
                            ? edu.start_date.year.toString()
                            : ""
                        }
                        onChange={(val) =>
                          handleChange(index, "year", val || "0", "start_date")
                        }
                        size={matches ? "xs" : "sm"}
                      />
                      <Select
                        placeholder={t("month")}
                        data={months}
                        value={
                          edu.start_date?.month
                            ? edu.start_date.month.toString()
                            : ""
                        }
                        onChange={(val) =>
                          handleChange(index, "month", val || "0", "start_date")
                        }
                        disabled={!edu.start_date?.year}
                        size={matches ? "xs" : "sm"}
                      />

                      <Select
                        placeholder={t("day")}
                        data={getDaysInMonth(
                          edu.start_date?.month || 0,
                          edu.start_date?.year || 0
                        )}
                        value={
                          edu.start_date?.day
                            ? edu.start_date.day.toString()
                            : ""
                        }
                        disabled={
                          !edu.start_date?.month || !edu.start_date?.year
                        }
                        onChange={(val) =>
                          handleChange(index, "day", val || "0", "start_date")
                        }
                        size={matches ? "xs" : "sm"}
                      />
                    </Flex>
                    {formErrors[index]?.start_date && (
                      <Text color="#B00020" size="xs" mt={4}>
                        {formErrors[index].start_date}
                      </Text>
                    )}
                    {formErrors[index]?.date_range && (
                      <Text color="#B00020" size="xs" mt={4}>
                        {formErrors[index].date_range}
                      </Text>
                    )}
                  </Grid.Col>

                  {/* End Date */}
                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ padding: 8 }}>
                    <Text fw={500} mb={4}>
                      {t("end_date")}
                    </Text>
                    <Flex gap="sm">
                      <Select
                        placeholder={t("year")}
                        data={years}
                        value={
                          edu.is_current
                            ? null
                            : edu.end_date?.year != null
                            ? edu.end_date.year.toString()
                            : null
                        }
                        onChange={(val) =>
                          handleChange(index, "year", val || "0", "end_date")
                        }
                        disabled={edu.is_current}
                        size={matches ? "xs" : "sm"}
                      />

                      <Select
                        placeholder={t("month")}
                        data={months}
                        value={
                          edu.is_current
                            ? null
                            : edu.end_date?.month != null
                            ? edu.end_date.month.toString()
                            : null
                        }
                        onChange={(val) =>
                          handleChange(index, "month", val || "0", "end_date")
                        }
                        disabled={!edu.end_date?.year || edu.is_current}
                        size={matches ? "xs" : "sm"}
                      />
                      <Select
                        placeholder={t("day")}
                        data={getDaysInMonth(
                          edu.end_date?.month || 0,
                          edu.end_date?.year || 0
                        )}
                        value={
                          edu.is_current
                            ? null
                            : edu.end_date?.day != null
                            ? edu.end_date.day.toString()
                            : null
                        }
                        onChange={(val) =>
                          handleChange(index, "day", val || "0", "end_date")
                        }
                        disabled={
                          !edu.end_date?.month ||
                          !edu.end_date?.year ||
                          edu.is_current
                        }
                        size={matches ? "xs" : "sm"}
                      />
                    </Flex>
                    {formErrors[index]?.end_date && (
                      <Text color="#B00020" size="xs" mt={4}>
                        {formErrors[index].end_date}
                      </Text>
                    )}

                    <Checkbox
                      mt="md"
                      label={t("currently_studying")}
                      checked={edu.is_current}
                      onChange={(event) =>
                        handleIsCurrentChange(
                          index,
                          event.currentTarget.checked
                        )
                      }
                      styles={{
                        body: {
                          alignItems: "center",
                        },
                      }}
                    />
                  </Grid.Col>
                </Grid>

                <ActionIcon
                  variant="subtle"
                  color="gray"
                  style={{ position: "absolute", top: 5, right: 5 }}
                  onClick={() => {
                    if (!edu.id) {
                      handleDelete(index);
                    } else {
                      setIsDeleteOpen(true);
                      setDeleteId(edu.id!);
                      setDeleteIndex(index);
                    }
                  }}
                >
                  <ClearIcon />
                </ActionIcon>

                <Flex
                  align="center"
                  justify={matches ? "center" : "end"}
                  mt="lg"
                  gap={20}
                >
                  {formErrors[index]?.global && (
                    <Text color="#B00020" size="xs" mt={4}>
                      {formErrors[index].global}
                    </Text>
                  )}
                  <Button
                    variant="light"
                    style={{ flexShrink: 0 }}
                    color="gray"
                    onClick={() => {
                      if (!edu.isNew) {
                        handleReset(index);
                      } else {
                        handleDelete(index, edu.id);
                      }
                    }}
                    disabled={!isChanged}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant="light"
                    style={{ flexShrink: 0 }}
                    color="green"
                    onClick={() => handleSave(index, edu)}
                    disabled={!isChanged}
                  >
                    {t("save")}
                  </Button>
                </Flex>
              </Paper>
            );
          })}
          <div ref={scrollRef} />
        </Flex>
      )}
      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
          setDeleteIndex(null);
        }}
        onDelete={() => {
          if (deleteId !== null && deleteIndex !== null)
            handleDelete(deleteIndex, deleteId);
        }}
        title={t("delete_education_title")}
      />
    </Box>
  );
};

export default EducationEditingWidget;
